import { FC, useCallback, useState } from 'react';
import LoaderWrapper from '@/components/ui/LoaderWrapper/LoaderWrapper.tsx';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from '@mui/material';
import Toggle from '@/components/shared/Toggle/Toggle.tsx';
import useIntegrations from '@/api/useIntegrations.ts';
import IntegrationsTable from '@/components/functional/IntegrationsTable/IntegrationsTable.tsx';
import AddIntegrationModal from '@/components/modals/Integration/AddIntegrationModal.tsx';
import DeleteIntegrationModal from '@/components/modals/Integration/DeleteIntegrationModal.tsx';
import EditIntegrationModal from '@/components/modals/Integration/EditIntegrationModal.tsx';

const IntegrationsPage: FC = () => {
  const { t } = useTranslation();
  const [selectedElements, setSelectedElements] = useState<number[]>([]);
  const {
    data: integrations,
    isLoading: isLoadingIntegrations,
    isError: isErrorIntegrations,
    error: errorIntegrations,
  } = useIntegrations();

  const handleSelectedElementChange = useCallback((selectedElements: number[]) => {
    setSelectedElements(selectedElements);
  }, []);

  const hasSelectedElement = selectedElements.length === 1;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap gap-4">
        <h1 className="text-3xl font-medium m-0 font-ubuntu">{t('pages.integrations.title')}</h1>
      </div>
      {isErrorIntegrations && <Alert severity="error">{errorIntegrations?.message}</Alert>}
      <div className="flex justify-end flex-wrap gap-2">
        <Toggle
          renderTrigger={({ toggle }) => (
            <Button onClick={toggle} color="info" variant="contained">
              {t('button.add')}
            </Button>
          )}
          renderContent={({ isOpen, handleClose }) => <AddIntegrationModal isOpen={isOpen} onClose={handleClose} />}
        />
        <Toggle
          renderTrigger={({ toggle }) => (
            <Button onClick={toggle} color="info" variant="contained" disabled={!hasSelectedElement}>
              {t('button.edit')}
            </Button>
          )}
          renderContent={({ isOpen, handleClose }) => (
            <EditIntegrationModal isOpen={isOpen} onClose={handleClose} integrationId={selectedElements[0]} />
          )}
        />
        <Toggle
          renderTrigger={({ toggle }) => (
            <Button onClick={toggle} color="error" variant="contained" disabled={!hasSelectedElement}>
              {t('button.delete')}
            </Button>
          )}
          renderContent={({ isOpen, handleClose }) => (
            <DeleteIntegrationModal isOpen={isOpen} onClose={handleClose} selectedElement={selectedElements[0]} />
          )}
        />
      </div>
      <div>
        <LoaderWrapper isLoading={isLoadingIntegrations}>
          <IntegrationsTable
            rows={integrations ?? []}
            selectable={true}
            onSelectedChange={handleSelectedElementChange}
          />
        </LoaderWrapper>
      </div>
    </div>
  );
};

export default IntegrationsPage;
